import React, { useEffect, useMemo } from 'react';
import { Card, Link, Text, Select } from 'v3/components';
import { useLoadGro } from '../../context';
import { LoadContext } from 'v3/pages/Load/Register/controller';
import TravelRouteCard from 'v3/components/TravelRouteCard';
import {
  insertWaypointsIntoPoints,
  normalizeWaypoints,
} from 'v3/components/TravelRouteCard/utils/points';
import { Col, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';

const [, useLoad] = LoadContext;

export function LocationCard({
  showRouteErrors,
  routeErrorMessage,
  routeErrorPointIds = [],
}) {

  const {
    selectedPolicy,
    errorsGro,
    setErrorsGro,
    destinationsError,
    setDestinationsError,
    countryOptions,
    selectedPolicyProduct,
    vehicleTypes,
    bodiesTypes,
    implementTypes,
    tollsPlazaInfos,
    setTollsPlazaInfos,
    setTollsPlazaLoading,
    handleGetSelectedPolicy
  } = useLoadGro();
  const { data, setData } = useLoad();
  const params = useParams();

    useEffect(() => {
    if(params.clone_id){
      handleGetSelectedPolicy(data?.policy?.id);
    }
  }, [params.clone_id]);

  const VehicleGroupErrorMessage = ({ text }) => (
    <>
      <Text type="span" color="#696969">
        {text}{' '}
      </Text>
      <Text type="span" color="red" fontSize="10px">
        - Esse tipo de veículo permanece a outro grupo de veículo
      </Text>
    </>
  );

  const shouldDisplayImplement = useMemo(
    () => data?.vehicleTypes?.some(selected => selected.qtdPlates > 1),
    [data?.vehicleTypes]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const points = useMemo(() => {
    const pts = [];

    if (data?.origin?.lat && data?.origin?.lng) {
      pts.push(data?.origin);
    }

    if (data?.destinations?.length > 0) {
      pts.push(...data?.destinations.filter(destination => destination?.lat && destination?.lng));
    }

    const waypoints = normalizeWaypoints(data?.polylineWaypoints);
    return insertWaypointsIntoPoints(pts, waypoints, {
      defaultCountry: countryOptions?.origin?.[0],
    });
  }, [
    data.origin,
    data.destinations,
    data?.polylineWaypoints,
    countryOptions?.origin,
  ]);

  const handleChangeRoute = ({ polyline, polylineWaypoints }) => {
    setData(prev => ({
      ...prev,
      polylineRoute: polyline,
      polylineWaypoints,
    }));
  };

  const clearRouteDateErrors = () => {
    setDestinationsError?.({});
    setErrorsGro?.(old => {
      const nextErrors = { ...(old || {}) };

      Object.entries(nextErrors).forEach(([key, message]) => {
        const isRouteDateField =
          /^origin\.(start_schedule|scheduled_time)$/.test(key) ||
          /^destinations\[\d+\]\.(start_schedule|scheduled_time)$/.test(key) ||
          ((key === 'origin' || /^destinations\[\d+\]$/.test(key)) &&
            String(message || '').toLowerCase().includes('data'));

        if (isRouteDateField) {
          delete nextErrors[key];
        }
      });

      return nextErrors;
    });
  };

  const handleChangeAddress = ({ origin, destinations, polyline, polylineWaypoints }) => {
    clearRouteDateErrors();

    setData(prev => ({
      ...prev,
      origin,
      destinations: [...destinations],
      polylineRoute: polyline,
      polylineWaypoints,
    }));

  };

  const validateRetroactive = useMemo(() => {
    if (!data?.costCenter) {
      return true;
    }

    return data?.costCenter?.limit_schedule;
  }, [data?.costCenter]);

  const vehicleTypesMemo = useMemo(() => {
    const productVehicleGroups = selectedPolicyProduct?.vehicles || [];

    return vehicleTypes.map(type => ({
      ...type,
      isDisabled: !hasGroupWithSelectedItems(productVehicleGroups, type),
    }));
  }, [
    data?.vehicleTypes,
    data?.vehicleImplements,
    data?.vehicleBodies,
    selectedPolicyProduct,
  ]);

  const implementTypesMemo = useMemo(() => {
    const productVehicleGroups = selectedPolicyProduct?.vehicles || [];

    return implementTypes.map(type => ({
      ...type,
      isDisabled: !hasGroupWithSelectedItems(productVehicleGroups, type),
    }));
  }, [
    data?.vehicleTypes,
    data?.vehicleImplements,
    data?.vehicleBodies,
    selectedPolicyProduct,
  ]);

  const bodiesTypesMemo = useMemo(() => {
    const productVehicleGroups = selectedPolicyProduct?.vehicles || [];

    return bodiesTypes.map(type => ({
      ...type,
      isDisabled: !hasGroupWithSelectedItems(productVehicleGroups, type),
    }));
  }, [
    data?.vehicleTypes,
    data?.vehicleImplements,
    data?.vehicleBodies,
    selectedPolicyProduct,
  ]);

  function hasAllItemsInGroup(group, items) {
    return items.every(item =>
      group.vehicle_types.find(i => i.id === item.id && i.type === item.type)
    );
  }

  function hasGroupWithSelectedItems(vehicleGroups, item) {
    if (!Array.isArray(vehicleGroups) || vehicleGroups.length === 0) {
      return false;
    }

    for (const group of vehicleGroups) {
      const hasVehicleTypes = hasAllItemsInGroup(group, [
        ...(!params.clone_id ? data?.vehicleTypes || [] : []),
        item,
      ]);
      const hasVehicleBodies = hasAllItemsInGroup(group, [
        ...(!params.clone_id ? data?.vehicleBodies || [] : []),
        item,
      ]);
      const hasVehicleImplements = hasAllItemsInGroup(group, [
        ...(!params.clone_id ? data?.vehicleImplements || [] : []),
        item,
      ]);
      if (hasVehicleTypes && hasVehicleBodies && hasVehicleImplements) {
        return true;
      }
    }

    return false;
  }

  const destinationsWithMissingCity = useMemo(() => {
    const missingIndexes = [];
    if (Array.isArray(data?.destinations)) {
      data.destinations.forEach((dest, idx) => {
        if (!dest?.city || !dest?.city?.id) {
          missingIndexes.push(idx + 1);
        }
      });
    }
    return missingIndexes;
  }, [data?.destinations]);

  return (
    <>
      <Card style={{ position: 'relative', zIndex: 10 }}>
        <Row className="mb-2">
          <Col>
            <Text
              color="#464E5F"
              type="header"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              Veículos e Implementos
              {selectedPolicy?.id && (
                <Link
                  to={`/apolices/${selectedPolicy.id}`}
                  target="_blank"
                  style={{ color: '#0D6EFD', textDecoration: 'none' }}
                >
                  <Text type="label" color="#0D6EFD">
                    ver apólice
                  </Text>
                </Link>
              )}
            </Text>
          </Col>
        </Row>
        <Row>
          <Col md={4} className="mb-3">
            <Select
              label="Tipo de Veículo *"
              options={vehicleTypesMemo}
              getOptionLabel={option =>
                option.isDisabled ? (
                  <VehicleGroupErrorMessage text={option.name} />
                ) : (
                  option.name
                )
              }
              getOptionValue={option => option.id}
              value={data?.vehicleTypes}
              onChange={value => {
                setData(old => ({
                  ...old,
                  vehicleTypes: value || [],
                  vehicleImplements: [],
                  vehicleBodies: [],
                  cargoValue: '0',
                }));
              }}
              horizontal
              multiple
              error={errorsGro.vehicleTypes}
              id="select-gro-veiculos"
            />
          </Col>
          <Col md={4} className="mb-3">
            <Select
              label="Tipo de implemento *"
              options={implementTypesMemo}
              getOptionLabel={option =>
                option.isDisabled ? (
                  <VehicleGroupErrorMessage text={option.name} />
                ) : (
                  option.name
                )
              }
              getOptionValue={option => option.id}
              value={data?.vehicleImplements}
              onChange={value =>
                setData(old => ({
                  ...old,
                  vehicleImplements: value || [],
                  cargoValue: '0',
                }))
              }
              multiple
              error={errorsGro.vehicleImplements}
              id="select-gro-implemento"
              isDisabled={
                !shouldDisplayImplement || !data?.vehicleTypes?.length
              }
            />
          </Col>
          <Col md={4} className="mb-3">
            <Select
              label="Tipo de carroceria *"
              options={bodiesTypesMemo}
              getOptionLabel={option =>
                option.isDisabled ? (
                  <VehicleGroupErrorMessage text={option.name} />
                ) : (
                  option.name
                )
              }
              getOptionValue={option => option.id}
              value={data?.vehicleBodies}
              onChange={value =>
                setData(old => ({
                  ...old,
                  vehicleBodies: value || [],
                  cargoValue: '0',
                }))
              }
              multiple
              error={errorsGro.vehicleBodies}
              isDisabled={!data?.vehicleTypes?.length}
              id="select-gro-carroceria"
            />
          </Col>
        </Row>
      </Card>
      <TravelRouteCard
        points={points}
        polyline={data?.polylineRoute}
        polylineWaypoints={data?.polylineWaypoints}
        onChangeRoute={handleChangeRoute}
        onChangeAddress={handleChangeAddress}
        validateRetroactive={validateRetroactive}
        showRouteErrors={showRouteErrors}
        routeErrorMessage={routeErrorMessage}
        externalRouteErrorPointIds={[...routeErrorPointIds, ...destinationsWithMissingCity]}
        loadCreatedGro={data} 
        tollsPlazaInfos={tollsPlazaInfos}
        setTollsPlazaInfos={setTollsPlazaInfos}
        setTollsPlazaLoadingGro={setTollsPlazaLoading}
        errorsGro={errorsGro}
        destinationsError={destinationsError}
        setDataLoad={setData}
        componentTitle={
          <Text
            color="#464E5F"
            type="header"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            Percurso
            {selectedPolicy?.id && (
              <Link
                to={`/apolices/${selectedPolicy.id}`}
                target="_blank"
                style={{ color: '#0D6EFD', textDecoration: 'none' }}
              >
                <Text type="label" color="#0D6EFD">
                  ver apólice
                </Text>
              </Link>
            )}
          </Text>
        }
      />
    </>
  );
}
